import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Avatar,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Modal,
  RadioGroup,
  Radio,
  Fade,
  Backdrop,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  ShoppingBag as OrdersIcon,
  LocationOn as AddressIcon,
  Favorite as WishlistIcon,
  CardGiftcard as VoucherIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import PasswordRules from '../../components/common/PasswordRules';
import { passwordMeetsPolicy } from '../../utils/passwordPolicy';
import { useDispatch, useSelector } from 'react-redux';
import authService from '../services/authService';
import accountService from '../services/accountService';
import orderDetailsService from '../services/orderDetailsService';
import OrderDetails from './OrderDetails';
import secureLocalStorage from 'react-secure-storage';
import { profileApi } from '../api/profile';
import TextIcon from '../../components/common/TextIcon';
import Seo from '../components/Seo';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateCart = useSelector((state) => state.cart);
  const cart = stateCart?.cart;
  const { businessName, evoucherEnabled } = useSelector((state) => state.configs);
  const { logo } = useSelector((state) => state.template);

  const [tabValue, setTabValue] = useState(0);
  const [isLogin, setIsLogin] = useState(!location.state?.register);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetMethod, setResetMethod] = useState('phone');
  const [resetStep, setResetStep] = useState(1); // 1: select method, 2: enter code, 3: new password
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');

  // Login form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Forced pin->password migration (backend returned passwordSetupRequired)
  const [setupToken, setSetupToken] = useState(null);
  const [setupNewPassword, setSetupNewPassword] = useState('');
  const [setupConfirmPassword, setSetupConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Profile data states
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');

  // Orders data states
  const [ordersData, setOrdersData] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(10);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersActiveFilter, setOrdersActiveFilter] = useState('');

  // Addresses data states
  const [addressesData, setAddressesData] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState('');
  const [, setEditingAddressId] = useState(null);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    address: '',
    city: '',
    county: '',
    postalCode: '',
    apartment: ''
  });
  const [, setEditAddress] = useState({
    address: '',
    city: '',
    county: '',
    postalCode: '',
    apartment: ''
  });

  // Wishlist data states
  const [wishlistData, setWishlistData] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistError, setWishlistError] = useState('');
  const [clearWishlistConfirmOpen, setClearWishlistConfirmOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Settings states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');
  const [emailPreferences, setEmailPreferences] = useState({
    newsletter: true,
    orderUpdates: true,
    promotionalEmails: false
  });
  const [emailPreferencesLoading, setEmailPreferencesLoading] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  // Order Details View state
  const [viewingOrderId, setViewingOrderId] = useState(null);

  // Vouchers data states
  const [vouchersData, setVouchersData] = useState([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [vouchersError, setVouchersError] = useState('');

  // Check if user is logged in on component mount
  useEffect(() => {
    const checkLoginStatus = async () => {
      if (authService.isLoggedIn()) {
        setIsLoggedIn(true);
        const user = authService.getCurrentUser();
        if (user) {
          setEmail(user.email || '');
        }
      }
    };
    checkLoginStatus();
  }, []);

  // Fetch profile when profile tab becomes active
  useEffect(() => {
    if (tabValue === 0 && isLoggedIn && !profileData) {
      const fetchProfileData = async () => {
        setProfileLoading(true);
        const result = await accountService.fetchCustomerProfile();
        if (result.success) {
          setProfileData(result.data);
          setFirstName(result.data.firstName);
          setLastName(result.data.lastName);
          setEmail(result.data.email);
          setPhone(result.data.phone);
          setDateOfBirth(result.data.dateOfBirth);
        } else {
          setProfileError(result.error);
        }
        setProfileLoading(false);
      };
      fetchProfileData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, isLoggedIn]);

  // Fetch orders when orders tab becomes active or pagination changes
  useEffect(() => {
    if (tabValue === 1 && isLoggedIn) {
      const fetchOrdersData = async () => {
        setOrdersLoading(true);
        const result = await accountService.fetchCustomerOrders(ordersPage, ordersPageSize, ordersActiveFilter);
        if (result.success) {
          setOrdersData(result.orders);
          setOrdersTotal(result.total);
        } else {
          setOrdersError(result.error);
        }
        setOrdersLoading(false);
      };
      fetchOrdersData();
    }
  }, [tabValue, isLoggedIn, ordersPage, ordersPageSize, ordersActiveFilter]);

  // Fetch wishlist when wishlist tab becomes active
  useEffect(() => {
    if (tabValue === 3 && isLoggedIn && wishlistData.length === 0) {
      const fetchWishlistData = async () => {
        setWishlistLoading(true);
        const result = await accountService.fetchWishlist();
        if (result.success) {
          setWishlistData(result.items);
        } else {
          setWishlistError(result.error);
        }
        setWishlistLoading(false);
      };
      fetchWishlistData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, isLoggedIn]);

  // Fetch vouchers when vouchers tab becomes active
  useEffect(() => {
    if (tabValue === 5 && isLoggedIn) {
      const fetchVouchersData = async () => {
        setVouchersLoading(true);
        const result = await accountService.fetchCustomerVouchers();
        if (result.success) {
          setVouchersData(result.vouchers);
          setVouchersError('');
        } else {
          setVouchersError(result.error);
        }
        setVouchersLoading(false);
      };
      fetchVouchersData();
    }
  }, [tabValue, isLoggedIn]);

  // Fetch addresses when addresses tab becomes active
  useEffect(() => {
    if (tabValue === 2 && isLoggedIn) {
      const fetchAddressesData = async () => {
        setAddressesLoading(true);
        const customerId = secureLocalStorage.getItem('customerId');
        const result = await profileApi.getCustomerAddresses(customerId);
        if (result.success) {
          setAddressesData(result.data);
        } else {
          setAddressesError(result.error);
        }
        setAddressesLoading(false);
      };
      fetchAddressesData();
    }
  }, [tabValue, isLoggedIn]);

  // Geometric background style
  const backgroundStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(135deg, color-mix(in srgb, var(--primary) 3%, transparent) 0%, color-mix(in srgb, var(--primary) 1%, transparent) 100%),
      repeating-linear-gradient(45deg, transparent, transparent 35px, color-mix(in srgb, var(--primary) 2%, transparent) 35px, color-mix(in srgb, var(--primary) 2%, transparent) 70px),
      repeating-linear-gradient(-45deg, transparent, transparent 35px, color-mix(in srgb, var(--primary) 1%, transparent) 35px, color-mix(in srgb, var(--primary) 1%, transparent) 70px)
    `,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 50%, color-mix(in srgb, var(--primary) 5%, transparent) 0%, transparent 50%),
        radial-gradient(circle at 80% 80%, color-mix(in srgb, var(--primary) 4%, transparent) 0%, transparent 50%),
        radial-gradient(circle at 40% 20%, color-mix(in srgb, var(--primary) 3%, transparent) 0%, transparent 50%)
      `,
    },
    zIndex: -1,
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (setupToken) {
      await handlePasswordSetup();
    } else if (isLogin) {
      await handleLogin();
    } else {
      await handleRegister();
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate email
    if (!authService.validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Login accepts a password or a legacy 4-digit PIN; only require non-empty.
    if (!password) {
      setErrorMessage('Password is required');
      setLoading(false);
      return;
    }

    // Call login service
    const result = await authService.login(email, password, dispatch, navigate, location, cart);

    if (result.success) {
      setSuccessMessage(result.message);
      setIsLoggedIn(true);
      // Navigation is handled by the service
    } else if (result.setupRequired) {
      // The account still uses a PIN — a password must be set before a session exists.
      setSetupToken(result.setupToken);
      setSetupNewPassword('');
      setSetupConfirmPassword('');
      setErrorMessage('');
      setSuccessMessage('');
    } else {
      setErrorMessage(result.message);
    }

    setLoading(false);
  };

  const handlePasswordSetup = async () => {
    setErrorMessage('');
    if (!passwordMeetsPolicy(setupNewPassword)) {
      setErrorMessage('Password does not meet the requirements below');
      return;
    }
    if (setupNewPassword !== setupConfirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    setLoading(true);
    const setup = await authService.setupPassword(setupToken, setupNewPassword);
    if (!setup.success) {
      setErrorMessage(setup.message);
      if (setup.expired) setSetupToken(null);
      setLoading(false);
      return;
    }
    // Log straight in with the new password.
    const result = await authService.login(email, setupNewPassword, dispatch, navigate, location, cart);
    if (result.success) {
      setSetupToken(null);
      setSuccessMessage('Password set. Welcome back!');
      setIsLoggedIn(true);
    } else {
      setSetupToken(null);
      setSuccessMessage('Password set. Please log in with your new password.');
      setPassword('');
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validate all fields
    if (!firstName.trim()) {
      setErrorMessage('First name is required');
      setLoading(false);
      return;
    }

    if (!lastName.trim()) {
      setErrorMessage('Last name is required');
      setLoading(false);
      return;
    }

    if (!authService.validateEmail(email)) {
      setErrorMessage('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (!phone || phone.length < 10) {
      setErrorMessage('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    // Registration requires a strong password (policy mirror of the backend).
    if (!passwordMeetsPolicy(password)) {
      setErrorMessage('Password does not meet the requirements below');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    // Prepare registration data
    const userData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone,
      password: password
    };

    // Call registration service
    const result = await authService.register(userData, dispatch, navigate, cart);

    if (result.success) {
      setSuccessMessage(result.message);
      // Auto-login after registration is handled by the service
    } else {
      setErrorMessage(result.message);
    }

    setLoading(false);
  };

  const handleLogout = async () => {
    const result = await authService.logout(dispatch);
    if (result.success) {
      setIsLoggedIn(false);
      setTabValue(0);
      navigate('/');
    }
  };

  const handleForgotPassword = () => {
    setForgotPasswordOpen(true);
    setResetStep(1);
    setVerificationCode('');
  };

  const handleSendCode = () => {
    // Simulate sending verification code
    setResetStep(2);
  };

  const handleVerifyCode = () => {
    // Simulate code verification
    if (verificationCode.length === 6) {
      setResetStep(3);
    }
  };

  const handleResetPassword = () => {
    // Simulate password reset
    setForgotPasswordOpen(false);
    setResetStep(1);
    alert('Password reset successfully! You can now login with your new password.');
  };

  const handleClearWishlistClick = () => {
    setClearWishlistConfirmOpen(true);
  };

  const handleClearWishlistConfirm = async () => {
    setClearWishlistConfirmOpen(false);
    setIsClearing(true);

    try {
      const result = await accountService.clearWishlist();
      if (result.success) {
        setWishlistData([]);
        setWishlistError('');
      } else {
        setWishlistError(result.error);
      }
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearWishlistCancel = () => {
    setClearWishlistConfirmOpen(false);
  };

  if (!isLoggedIn) {
    return (
      <>
        <Seo title="My Account" noindex />
        <Box sx={backgroundStyle} />
        <Container maxWidth="sm" sx={{ py: 4, position: 'relative' }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid color-mix(in srgb, var(--primary) 10%, transparent)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
              {logo?.logoType === 'logo' && logo?.logoPath ? (
                <Box
                  component="img"
                  src={logo.logoPath}
                  alt={`${logo?.logoText || businessName || 'Store'} logo`}
                  sx={{
                    width: '180px',
                    height: '100px',
                    objectFit: 'contain',
                    margin: '0 auto',
                    display: 'block',
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <TextIcon text={logo?.logoText || businessName?.charAt(0)?.toUpperCase() || 'A'} width={70} height={70} />
                </Box>
              )}
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </Typography>
              <Typography color="text.secondary">
                {isLogin ? 'Sign in to access your account' : `Join ${businessName || 'us'} today`}
              </Typography>
            </Box>

          {/* Error and Success Messages */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {setupToken && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your account still uses a 4-digit PIN. Set a password to continue — you'll use it to log in from now on.
                </Alert>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  variant="outlined"
                  margin="normal"
                  value={setupNewPassword}
                  onChange={(e) => setSetupNewPassword(e.target.value.replace(/\s/g, ''))}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  inputProps={{ maxLength: 128 }}
                />
                <PasswordRules password={setupNewPassword} />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  variant="outlined"
                  margin="normal"
                  value={setupConfirmPassword}
                  onChange={(e) => setSetupConfirmPassword(e.target.value.replace(/\s/g, ''))}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  inputProps={{ maxLength: 128 }}
                />
              </>
            )}
            {!setupToken && !isLogin && (
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    variant="outlined"
                    margin="normal"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <TextField
                    fullWidth
                    label="Last Name"
                    variant="outlined"
                    margin="normal"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  variant="outlined"
                  margin="normal"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g., 0712345678"
                  required
                  disabled={loading}
                />
              </>
            )}

            {!setupToken && (<>
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => {
                  // No whitespace anywhere; legacy PINs still type fine on login.
                  setPassword(e.target.value.replace(/\s/g, ''));
                }}
                required
                disabled={loading}
                placeholder={isLogin ? 'Enter your password or PIN' : 'Enter your password'}
                inputProps={{ maxLength: 128 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {!isLogin && <PasswordRules password={password} />}

              {!isLogin && (
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  variant="outlined"
                  margin="normal"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value.replace(/\s/g, ''));
                  }}
                  required
                  disabled={loading}
                  placeholder="Re-enter your password"
                  inputProps={{ maxLength: 128 }}
                />
              )}
            </>)}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                mb: 1.5,
                py: 1,
                background: loading ? '#ccc' : 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                boxShadow: '0 4px 15px color-mix(in srgb, var(--primary) 30%, transparent)',
                '&:hover': {
                  background: loading ? '#ccc' : 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                  boxShadow: '0 6px 20px color-mix(in srgb, var(--primary) 40%, transparent)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Chip label="OR" size="small" sx={{ bgcolor: 'transparent' }} />
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => setIsLogin(!isLogin)}
              sx={{
                color: 'var(--primary)',
                borderColor: 'var(--primary)',
                py: 0.8,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'var(--button-hover)',
                  bgcolor: 'color-mix(in srgb, var(--primary) 5%, transparent)',
                },
              }}
            >
              {isLogin ? 'Create New Account' : 'Already have an account? Sign In'}
            </Button>

            {isLogin && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography
                  component="button"
                  onClick={handleForgotPassword}
                  sx={{ 
                    color: 'var(--primary)',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'var(--primary)',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  Forgot Password?
                </Typography>
              </Box>
            )}
          </form>
        </Paper>

        {/* Forgot Password Modal */}
        <Modal
          open={forgotPasswordOpen}
          onClose={() => setForgotPasswordOpen(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
            sx: { backdropFilter: 'blur(5px)' }
          }}
        >
          <Fade in={forgotPasswordOpen}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 450 },
                maxWidth: '90vw',
                p: 4,
                borderRadius: 2,
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                maxHeight: '90vh',
                overflow: 'auto',
              }}
            >
              <Box sx={{ position: 'relative' }}>
                {/* Close button */}
                <IconButton
                  onClick={() => setForgotPasswordOpen(false)}
                  sx={{
                    position: 'absolute',
                    right: -16,
                    top: -16,
                    color: '#666',
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Step 1: Select Reset Method */}
                {resetStep === 1 && (
                  <>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Forgot Password?
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Select an option to reset your password. We will send a security code to the email address/phone number, please make sure it is correct.
                    </Typography>

                    <RadioGroup
                      value={resetMethod}
                      onChange={(e) => setResetMethod(e.target.value)}
                      sx={{ mb: 3 }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          mb: 2,
                          border: resetMethod === 'phone' ? '2px solid var(--primary)' : '1px solid #e0e0e0',
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': { borderColor: 'var(--primary)' },
                        }}
                        onClick={() => setResetMethod('phone')}
                      >
                        <FormControlLabel
                          value="phone"
                          control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ color: 'var(--primary)' }} />
                              <Box>
                                <Typography fontWeight={500}>Phone Number</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Receive code via SMS
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </Paper>

                      <Paper
                        sx={{
                          p: 2,
                          border: resetMethod === 'email' ? '2px solid var(--primary)' : '1px solid #e0e0e0',
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': { borderColor: 'var(--primary)' },
                        }}
                        onClick={() => setResetMethod('email')}
                      >
                        <FormControlLabel
                          value="email"
                          control={<Radio sx={{ color: 'var(--primary)', '&.Mui-checked': { color: 'var(--primary)' } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon sx={{ color: 'var(--primary)' }} />
                              <Box>
                                <Typography fontWeight={500}>Email Address</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Receive code via Email
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </Paper>
                    </RadioGroup>

                    {resetMethod === 'phone' && (
                      <TextField
                        fullWidth
                        label="Phone Number"
                        placeholder="+254 700 000 000"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}

                    {resetMethod === 'email' && (
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        placeholder="your@email.com"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        required
                      />
                    )}

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSendCode}
                      sx={{
                        mt: 3,
                        py: 1.2,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                        },
                      }}
                    >
                      Send Verification Code
                    </Button>
                  </>
                )}

                {/* Step 2: Enter Verification Code */}
                {resetStep === 2 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <IconButton onClick={() => setResetStep(1)} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                      </IconButton>
                      <Typography variant="h5" fontWeight="bold">
                        Enter Verification Code
                      </Typography>
                    </Box>
                    
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      We've sent a 6-digit code to your {resetMethod === 'phone' ? 'phone number' : 'email address'}. Please enter it below.
                    </Typography>

                    <TextField
                      fullWidth
                      label="Verification Code"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      variant="outlined"
                      margin="normal"
                      inputProps={{
                        maxLength: 6,
                        style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2rem' }
                      }}
                    />

                    <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                      Didn't receive the code?{' '}
                      <Typography
                        component="span"
                        sx={{
                          color: 'var(--primary)',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={handleSendCode}
                      >
                        Resend
                      </Typography>
                    </Typography>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleVerifyCode}
                      disabled={verificationCode.length !== 6}
                      sx={{
                        mt: 3,
                        py: 1.2,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                        },
                        '&.Mui-disabled': {
                          background: '#e0e0e0',
                        },
                      }}
                    >
                      Verify Code
                    </Button>
                  </>
                )}

                {/* Step 3: Reset Password */}
                {resetStep === 3 && (
                  <>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Reset Password
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                      Please enter your new password below.
                    </Typography>

                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      variant="outlined"
                      margin="normal"
                      required
                    />

                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      variant="outlined"
                      margin="normal"
                      required
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleResetPassword}
                      sx={{
                        mt: 3,
                        py: 1.2,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, var(--button-hover) 0%, #018580 100%)',
                        },
                      }}
                    >
                      Reset Password
                    </Button>
                  </>
                )}
              </Box>
            </Paper>
          </Fade>
        </Modal>
      </Container>
      </>
    );
  }

  return (
    <>
      <Seo title="My Account" noindex />
      <Box sx={backgroundStyle} />
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative' }}>
        <Grid container spacing={3}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 3,
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, minWidth: 0 }}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'var(--primary)', flexShrink: 0 }}>
                {profileData?.firstName?.charAt(0)}{profileData?.lastName?.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {profileData?.firstName} {profileData?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {profileData?.email}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <List>
              <ListItemButton
                selected={tabValue === 0}
                onClick={() => setTabValue(0)}
              >
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText primary="Profile" />
              </ListItemButton>
              
              <ListItemButton
                selected={tabValue === 1}
                onClick={() => setTabValue(1)}
              >
                <ListItemIcon><OrdersIcon /></ListItemIcon>
                <ListItemText primary="Orders" />
              </ListItemButton>
              
              <ListItemButton
                selected={tabValue === 2}
                onClick={() => setTabValue(2)}
              >
                <ListItemIcon><AddressIcon /></ListItemIcon>
                <ListItemText primary="Addresses" />
              </ListItemButton>
              
              <ListItemButton
                selected={tabValue === 3}
                onClick={() => setTabValue(3)}
              >
                <ListItemIcon><WishlistIcon /></ListItemIcon>
                <ListItemText primary="Wishlist" />
              </ListItemButton>
              
              {evoucherEnabled && (
                <ListItemButton
                  selected={tabValue === 5}
                  onClick={() => setTabValue(5)}
                >
                  <ListItemIcon><VoucherIcon /></ListItemIcon>
                  <ListItemText primary="My Vouchers" />
                </ListItemButton>
              )}

              <ListItemButton
                selected={tabValue === 4}
                onClick={() => setTabValue(4)}
              >
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>
              
              <Divider sx={{ my: 2 }} />
              
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            }}>
            {/* Profile Tab */}
            <TabPanel value={tabValue} index={0}>
              {profileLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress sx={{ color: 'var(--primary)' }} />
                </Box>
              ) : profileError ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {profileError}
                </Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                      My Profile
                    </Typography>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setEditProfile(!editProfile)}
                      sx={{ color: 'var(--primary)' }}
                    >
                      {editProfile ? 'Cancel' : 'Edit Profile'}
                    </Button>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={!editProfile}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={!editProfile}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={!editProfile}
                        variant="outlined"
                        type="email"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={!editProfile}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        disabled={!editProfile}
                        variant="outlined"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        slotProps={{ inputBase: { sx: { '& input[type="date"]': { cursor: !editProfile ? 'not-allowed' : 'auto' } } } }}
                      />
                    </Grid>
                  </Grid>

                  {editProfile && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        sx={{ bgcolor: 'var(--primary)', '&:hover': { bgcolor: 'var(--button-hover)' } }}
                        onClick={async () => {
                          const validation = accountService.validateProfileForm(firstName, lastName, email, phone);
                          if (validation.isValid) {
                            setLoading(true);
                            const result = await accountService.updateProfile(firstName, lastName, phone, email, dateOfBirth);
                            if (result.success) {
                              setSuccessMessage('Profile updated successfully');
                              setEditProfile(false);
                              setTimeout(() => setSuccessMessage(''), 3000);
                            } else {
                              setErrorMessage(result.error);
                              setTimeout(() => setErrorMessage(''), 3000);
                            }
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : 'Save Changes'}
                      </Button>
                      <Button variant="outlined" onClick={() => setEditProfile(false)}>
                        Cancel
                      </Button>
                    </Box>
                  )}

                  {successMessage && (
                    <Alert severity="success" sx={{ mt: 3 }}>
                      {successMessage}
                    </Alert>
                  )}
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                      {errorMessage}
                    </Alert>
                  )}
                </>
              )}
            </TabPanel>

            {/* Orders Tab */}
            <TabPanel  value={tabValue} index={1}>
              {viewingOrderId ? (
                <OrderDetails
                  orderId={viewingOrderId}
                  onBack={() => setViewingOrderId(null)}
                />
              ) : (
                <>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Order History
                  </Typography>

                  {/* Status Filter Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                {['', 'Received', 'Processing', 'Delivered'].map((status) => (
                  <Button
                    key={status || 'all'}
                    variant={ordersActiveFilter === status ? 'contained' : 'outlined'}
                    onClick={() => {
                      setOrdersActiveFilter(status);
                      setOrdersPage(1); // Reset to first page when filtering
                    }}
                    sx={{
                      backgroundColor: ordersActiveFilter === status ? 'var(--primary)' : 'transparent',
                      color: ordersActiveFilter === status ? '#fff' : 'var(--primary)',
                      borderColor: 'var(--primary)',
                      '&:hover': {
                        backgroundColor: ordersActiveFilter === status ? 'var(--button-hover)' : 'color-mix(in srgb, var(--primary) 5%, transparent)'
                      }
                    }}
                  >
                    {status || 'All'}
                  </Button>
                ))}
              </Box>

              {ordersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress sx={{ color: 'var(--primary)' }} />
                </Box>
              ) : ordersError ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {ordersError}
                </Alert>
              ) : ordersData.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No orders found
                </Alert>
              ) : (
                <>
                  {/* Orders List */}
                  {ordersData.map((order) => (
                    <Paper
                      key={order._id}
                      elevation={2}
                      sx={{
                        p: 3,
                        mb: 2,
                        background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 2%, transparent) 0%, color-mix(in srgb, var(--primary) 1%, transparent) 100%)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      }}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary" display="block">Order ID</Typography>
                          <Typography fontWeight="bold" sx={{ cursor: 'pointer', color: 'var(--primary)' }} onClick={() => setViewingOrderId(order._id)}>
                            {order.orderNumber}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary" display="block">Date</Typography>
                          <Typography>
                            {new Date(order.createdAt).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary" display="block">Items</Typography>
                          <Typography fontWeight="600">
                            {order.items?.length || 0} {order.items?.length === 1 ? 'item' : 'items'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                          <Typography
                            sx={{
                              color: order.orderStatus === 'Delivered' ? '#4CAF50' : order.orderStatus === 'Processing' ? 'var(--primary)' : order.orderStatus === 'Pending' ? '#F44336' : order.orderStatus === 'Cancelled' ? '#795548' : 'var(--primary)',
                              fontWeight: 'medium',
                            }}
                          >
                            {orderDetailsService.getStatusLabel(order.orderStatus, order.deliveryMethod)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Typography variant="caption" color="text.secondary" display="block">Total</Typography>
                          <Typography fontWeight="bold">KSH {order.amount?.toLocaleString() || '0'}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              borderColor: 'var(--primary)',
                              color: 'var(--primary)',
                              borderRadius: '6px',
                              '&:hover': {
                                borderColor: 'var(--button-hover)',
                                backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)',
                              },
                            }}
                            onClick={() => setViewingOrderId(order._id)}
                          >
                            View Details
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}

                  {/* Pagination Controls */}
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 2 }, justifyContent: { xs: 'flex-start', sm: 'space-between' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                      {/* Items per page selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }} color="text.secondary">
                          Items per page:
                        </Typography>
                        <select
                          value={ordersPageSize}
                          onChange={(e) => {
                            setOrdersPageSize(parseInt(e.target.value));
                            setOrdersPage(1);
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #ccc',
                            cursor: 'pointer',
                            fontSize: '14px',
                            minWidth: '60px'
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </Box>

                      {/* Pagination info and buttons */}
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, width: { xs: '100%', sm: 'auto' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                        <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: { xs: 'normal', sm: 'nowrap' } }} color="text.secondary">
                          Showing {((ordersPage - 1) * ordersPageSize) + 1} to {Math.min(ordersPage * ordersPageSize, ordersTotal)} of {ordersTotal} orders
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={ordersPage === 1}
                            sx={{
                              borderColor: 'var(--primary)',
                              color: 'var(--primary)',
                              fontSize: { xs: '0.7rem', sm: '0.875rem' },
                              padding: { xs: '6px 12px', sm: '8px 16px' },
                              flex: { xs: 1, sm: 'auto' },
                              '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)' },
                              '&:disabled': { borderColor: '#ccc', color: '#ccc' }
                            }}
                            onClick={() => setOrdersPage(ordersPage - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={ordersPage * ordersPageSize >= ordersTotal}
                            sx={{
                              borderColor: 'var(--primary)',
                              color: 'var(--primary)',
                              fontSize: { xs: '0.7rem', sm: '0.875rem' },
                              padding: { xs: '6px 12px', sm: '8px 16px' },
                              flex: { xs: 1, sm: 'auto' },
                              '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)' },
                              '&:disabled': { borderColor: '#ccc', color: '#ccc' }
                            }}
                            onClick={() => setOrdersPage(ordersPage + 1)}
                          >
                            Next
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
                </>
              )}
            </TabPanel>

            {/* Addresses Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  Saved Addresses
                </Typography>
                {!showAddAddressForm && addressesData.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AddressIcon />}
                    onClick={() => setShowAddAddressForm(true)}
                    sx={{
                      bgcolor: 'var(--primary)',
                      color: '#FFFFFF',
                      fontWeight: 600,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      padding: '8px 20px',
                      transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        bgcolor: 'var(--button-hover)',
                        boxShadow: '0 4px 14px color-mix(in srgb, var(--primary) 25%, transparent)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    Add New Address
                  </Button>
                )}
              </Box>

              {addressesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress sx={{ color: 'var(--primary)' }} />
                </Box>
              ) : addressesError ? (
                <Alert
                  severity="error"
                  onClose={() => setAddressesError('')}
                  sx={{
                    mb: 3,
                    borderRadius: '8px',
                    fontWeight: 500,
                    '& .MuiAlert-message': {
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Please fix the following errors:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {addressesError}
                  </Typography>
                </Alert>
              ) : (
                <>
                  {/* Add Address Form */}
                  {showAddAddressForm && (
                    <Paper
                      sx={{
                        p: 3,
                        mb: 4,
                        borderRadius: '12px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E0E0E0',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                      }}
                    >
                      <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2.5, color: '#1A1A1A' }}>
                        Add New Address
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Street Address"
                            value={newAddress.address}
                            onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                            placeholder="e.g., 123 Main Street"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                '&:hover fieldset': { borderColor: 'var(--primary)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="City"
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                '&:hover fieldset': { borderColor: 'var(--primary)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="County"
                            value={newAddress.county}
                            onChange={(e) => setNewAddress({ ...newAddress, county: e.target.value })}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                '&:hover fieldset': { borderColor: 'var(--primary)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Postal Code"
                            value={newAddress.postalCode}
                            onChange={(e) => setNewAddress({ ...newAddress, postalCode: e.target.value })}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                '&:hover fieldset': { borderColor: 'var(--primary)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Apartment/Suite (Optional)"
                            value={newAddress.apartment}
                            onChange={(e) => setNewAddress({ ...newAddress, apartment: e.target.value })}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                '&:hover fieldset': { borderColor: 'var(--primary)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--primary)' },
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3.5, display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: 'var(--primary)',
                            color: '#FFFFFF',
                            fontWeight: 600,
                            borderRadius: '6px',
                            textTransform: 'none',
                            padding: '10px 24px',
                            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: 'var(--button-hover)',
                              boxShadow: '0 4px 14px color-mix(in srgb, var(--primary) 25%, transparent)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                          onClick={async () => {
                            const result = await accountService.addAddress(newAddress);
                            if (result.success) {
                              setShowAddAddressForm(false);
                              setNewAddress({ address: '', city: '', county: '', postalCode: '', apartment: '' });
                              // Refresh addresses
                              setAddressesData([...addressesData, result.data]);
                              setAddressesError('');
                            } else if (result.errors) {
                              // Keep form open on validation errors and show error message
                              setAddressesError(Object.values(result.errors).join(', '));
                              // Form stays open so user can fix the errors
                            } else {
                              // Keep form open on API errors and show error message
                              setAddressesError(result.error);
                              // Form stays open so user can try again
                            }
                          }}
                        >
                          Save Address
                        </Button>
                        <Button
                          variant="outlined"
                          sx={{
                            borderColor: 'var(--primary)',
                            color: 'var(--primary)',
                            fontWeight: 600,
                            borderRadius: '6px',
                            textTransform: 'none',
                            padding: '10px 24px',
                            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                              borderColor: 'var(--button-hover)',
                            }
                          }}
                          onClick={() => {
                            setShowAddAddressForm(false);
                            setNewAddress({ address: '', city: '', county: '', postalCode: '', apartment: '' });
                            setAddressesError('');
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Paper>
                  )}

                  {/* Addresses List */}
                  {addressesData.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        borderRadius: '12px',
                        border: '2px dashed color-mix(in srgb, var(--primary) 30%, transparent)',
                        backgroundColor: 'color-mix(in srgb, var(--primary) 2%, transparent)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 220,
                        cursor: 'pointer',
                        transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          borderColor: 'color-mix(in srgb, var(--primary) 50%, transparent)',
                          backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                        }
                      }}
                      onClick={() => setShowAddAddressForm(true)}
                    >
                      <AddressIcon sx={{
                        fontSize: 56,
                        color: '#E0E0E0',
                        mb: 2,
                        transition: 'all 250ms ease',
                      }} />
                      <Typography variant="h6" sx={{ color: '#9E9E9E', mb: 2, fontWeight: 500 }}>
                        No addresses saved yet
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddressIcon />}
                        sx={{
                          bgcolor: 'var(--primary)',
                          color: '#FFFFFF',
                          fontWeight: 600,
                          borderRadius: '6px',
                          textTransform: 'none',
                          padding: '8px 20px',
                          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            bgcolor: 'var(--button-hover)',
                            boxShadow: '0 4px 14px color-mix(in srgb, var(--primary) 25%, transparent)',
                          }
                        }}
                      >
                        Add Your First Address
                      </Button>
                    </Paper>
                  ) : (
                    <Grid container spacing={3}>
                      {addressesData.map((addr) => (
                        <Grid item xs={12} md={6} key={addr._id}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: '12px',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                              transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                              position: 'relative',
                              '&:hover': {
                                boxShadow: '0 10px 20px rgba(0,0,0,0.10)',
                                transform: 'translateY(-2px)',
                                borderColor: 'var(--primary)',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '3px',
                                  background: 'linear-gradient(90deg, var(--primary), #4DC9C5)',
                                  borderRadius: '12px 12px 0 0',
                                }
                              }
                            }}
                          >
                            {/* Primary Address Badge */}
                            {addr.isPrimary && (
                              <Box sx={{ mb: 2 }}>
                                <Chip
                                  label="Primary Address"
                                  size="small"
                                  sx={{
                                    backgroundColor: 'var(--primary)',
                                    color: '#FFFFFF',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 'auto',
                                    padding: '4px 8px',
                                  }}
                                />
                              </Box>
                            )}

                            {/* Address Content */}
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                              Address
                            </Typography>
                            <Typography sx={{ mb: 1.5, color: '#1A1A1A', fontWeight: 500 }}>
                              {addr.address}
                            </Typography>
                            {addr.apartment && (
                              <Typography sx={{ mb: 1, color: '#616161' }}>
                                {addr.apartment}
                              </Typography>
                            )}
                            <Typography sx={{ mb: 1, color: '#616161' }}>
                              {addr.city}, {addr.county}
                            </Typography>
                            <Typography sx={{ mb: 3, color: '#616161' }}>
                              {addr.postalCode}
                            </Typography>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: 'var(--primary)',
                                  color: 'var(--primary)',
                                  fontWeight: 600,
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  flex: 1,
                                  transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    backgroundColor: 'color-mix(in srgb, var(--primary) 8%, transparent)',
                                    borderColor: 'var(--button-hover)',
                                  }
                                }}
                                onClick={() => {
                                  setEditingAddressId(addr._id);
                                  setEditAddress({
                                    address: addr.address,
                                    city: addr.city,
                                    county: addr.county,
                                    postalCode: addr.postalCode,
                                    apartment: addr.apartment || ''
                                  });
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                color="error"
                                sx={{
                                  fontWeight: 600,
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  flex: 1,
                                  transition: 'all 250ms ease',
                                  '&:hover': {
                                    backgroundColor: 'rgba(220, 38, 38, 0.08)',
                                  }
                                }}
                                onClick={async () => {
                                  const result = await accountService.deleteAddress(addr._id);
                                  if (result.success) {
                                    setAddressesData(addressesData.filter(a => a._id !== addr._id));
                                    setAddressesError('');
                                  } else {
                                    setAddressesError(result.error);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}

                      {/* Add New Address Card */}
                      {!showAddAddressForm && (
                        <Grid item xs={12} md={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: '12px',
                              border: '2px dashed color-mix(in srgb, var(--primary) 30%, transparent)',
                              backgroundColor: 'color-mix(in srgb, var(--primary) 2%, transparent)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 200,
                              cursor: 'pointer',
                              transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                borderColor: 'color-mix(in srgb, var(--primary) 50%, transparent)',
                                backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                              }
                            }}
                            onClick={() => setShowAddAddressForm(true)}
                          >
                            <AddressIcon sx={{
                              fontSize: 48,
                              color: '#E0E0E0',
                              mb: 1.5,
                              transition: 'all 250ms ease',
                            }} />
                            <Button
                              variant="contained"
                              sx={{
                                bgcolor: 'var(--primary)',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                borderRadius: '6px',
                                textTransform: 'none',
                                padding: '8px 20px',
                                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  bgcolor: 'var(--button-hover)',
                                  boxShadow: '0 4px 14px color-mix(in srgb, var(--primary) 25%, transparent)',
                                }
                              }}
                            >
                              Add Address
                            </Button>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </>
              )}
            </TabPanel>

            {/* Wishlist Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                  My Wishlist ({wishlistData.length || 0} items)
                </Typography>
                {wishlistData.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleClearWishlistClick}
                    disabled={isClearing}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              {wishlistLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress sx={{ color: 'var(--primary)' }} />
                </Box>
              ) : wishlistError ? (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setWishlistError('')}>{wishlistError}</Alert>
              ) : wishlistData.length === 0 ? (
                <Paper
                  elevation={1}
                  sx={{
                    p: 4,
                    border: '2px dashed color-mix(in srgb, var(--primary) 30%, transparent)',
                    background: 'color-mix(in srgb, var(--primary) 2%, transparent)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 250,
                    borderRadius: '12px'
                  }}
                >
                  <WishlistIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
                  <Typography color="text.secondary" gutterBottom>No items in your wishlist yet</Typography>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      bgcolor: 'var(--primary)',
                      '&:hover': { bgcolor: 'var(--button-hover)' }
                    }}
                    onClick={() => navigate('/products')}
                  >
                    Browse Products
                  </Button>
                </Paper>
              ) : (
                <Grid container spacing={2}>
                  {wishlistData.map((item) => (
                    <Grid size={12} key={item._id || item.id}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 2,
                          boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                          borderRadius: '12px',
                          transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 10px 20px rgba(0,0,0,0.10)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 250 }}>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              bgcolor: '#f5f5f5',
                              borderRadius: '6px',
                              backgroundImage: item.image ? `url(${item.image})` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              flexShrink: 0,
                              border: '1px solid rgba(0,0,0,0.08)'
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography fontWeight="600" sx={{ mb: 0.5 }}>
                              {item.name || item.productName || 'Product'}
                            </Typography>
                            <Typography color="var(--primary)" variant="h6" fontWeight="bold">
                              KSH {(item.price || item.productPrice || '0').toLocaleString()}
                            </Typography>
                            {item.sku && (
                              <Typography variant="caption" color="text.secondary">
                                SKU: {item.sku}
                              </Typography>
                            )}
                            {item.stock !== undefined && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}
                                  size="small"
                                  color={item.stock > 0 ? 'success' : 'error'}
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={(item.stock === 0)}
                            sx={{
                              bgcolor: 'var(--primary)',
                              '&:hover': { bgcolor: 'var(--button-hover)' },
                              '&:disabled': { bgcolor: '#e0e0e0', color: '#999' }
                            }}
                            onClick={() => {
                              // Add to cart functionality would go here
                            }}
                          >
                            Add to Cart
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={async () => {
                              const result = await accountService.removeFromWishlist(item._id || item.id);
                              if (result.success) {
                                setWishlistData(wishlistData.filter(w => (w._id || w.id) !== (item._id || item.id)));
                                setWishlistError('');
                              } else {
                                setWishlistError(result.error || 'Failed to remove from wishlist');
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Settings Tab */}
            <TabPanel value={tabValue} index={4}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Account Settings
              </Typography>

              {/* Change Password Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Change Password
                </Typography>
                {passwordChangeError && (
                  <Alert severity="error" sx={{ mb: 2 }}>{passwordChangeError}</Alert>
                )}
                {passwordChangeSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>{passwordChangeSuccess}</Alert>
                )}
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                      variant="outlined"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={passwordChangeLoading}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      variant="outlined"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={passwordChangeLoading}
                      helperText="Minimum 4 characters"
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      variant="outlined"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      disabled={passwordChangeLoading}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  loading={passwordChangeLoading}
                  disabled={passwordChangeLoading}
                  sx={{ mt: 2, bgcolor: 'var(--primary)', '&:hover': { bgcolor: 'var(--button-hover)' } }}
                  onClick={async () => {
                    setPasswordChangeError('');
                    setPasswordChangeSuccess('');
                    setPasswordChangeLoading(true);

                    const result = await accountService.changePassword(
                      currentPassword,
                      newPassword,
                      confirmNewPassword
                    );

                    if (result.success) {
                      setPasswordChangeSuccess(result.message);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmNewPassword('');
                      setTimeout(() => setPasswordChangeSuccess(''), 3000);
                    } else {
                      setPasswordChangeError(result.error);
                    }
                    setPasswordChangeLoading(false);
                  }}
                >
                  {passwordChangeLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Email Preferences Section */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Email Preferences
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailPreferences.newsletter}
                        onChange={(e) => setEmailPreferences({
                          ...emailPreferences,
                          newsletter: e.target.checked
                        })}
                      />
                    }
                    label="Newsletter - Stay updated with our latest products and offers"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailPreferences.orderUpdates}
                        onChange={(e) => setEmailPreferences({
                          ...emailPreferences,
                          orderUpdates: e.target.checked
                        })}
                      />
                    }
                    label="Order updates - Get notified about your order status"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={emailPreferences.promotionalEmails}
                        onChange={(e) => setEmailPreferences({
                          ...emailPreferences,
                          promotionalEmails: e.target.checked
                        })}
                      />
                    }
                    label="Promotional emails - Receive special offers and discounts"
                  />
                </FormGroup>
                <Button
                  variant="contained"
                  loading={emailPreferencesLoading}
                  disabled={emailPreferencesLoading}
                  sx={{ mt: 2, bgcolor: 'var(--primary)', '&:hover': { bgcolor: 'var(--button-hover)' } }}
                  onClick={async () => {
                    setEmailPreferencesLoading(true);
                    const result = await accountService.updateEmailPreferences(emailPreferences);
                    if (!result.success) {
                      setPasswordChangeError(result.error);
                    }
                    setEmailPreferencesLoading(false);
                  }}
                >
                  {emailPreferencesLoading ? 'Saving...' : 'Save Preferences'}
                </Button>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Delete Account Section */}
              <Box>
                <Typography variant="h6" color="error" gutterBottom>
                  Delete Account
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                  Once you delete your account, there is no going back. Please be certain.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  sx={{ mt: 2 }}
                  onClick={() => setShowDeleteAccountModal(true)}
                >
                  Delete Account
                </Button>
              </Box>

              {/* Delete Account Modal */}
              <Modal
                open={showDeleteAccountModal}
                onClose={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteAccountPassword('');
                }}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{ backdrop: { timeout: 500 } }}
              >
                <Fade in={showDeleteAccountModal}>
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: { xs: '90%', sm: 400 },
                      p: 3,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h6" component="h2" gutterBottom>
                      Confirm Account Deletion
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      This action cannot be undone. Your account and all associated data will be permanently deleted.
                    </Typography>
                    <TextField
                      fullWidth
                      label="Enter your password to confirm"
                      type="password"
                      variant="outlined"
                      value={deleteAccountPassword}
                      onChange={(e) => setDeleteAccountPassword(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          setShowDeleteAccountModal(false);
                          setDeleteAccountPassword('');
                        }}
                        disabled={deleteAccountLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        color="error"
                        loading={deleteAccountLoading}
                        disabled={deleteAccountLoading || !deleteAccountPassword}
                        onClick={async () => {
                          setDeleteAccountLoading(true);
                          const result = await accountService.deleteAccount(deleteAccountPassword);
                          if (result.success) {
                            setShowDeleteAccountModal(false);
                            setIsLoggedIn(false);
                            navigate('/');
                          } else {
                            setPasswordChangeError(result.error);
                          }
                          setDeleteAccountLoading(false);
                        }}
                      >
                        {deleteAccountLoading ? 'Deleting...' : 'Delete Account'}
                      </Button>
                    </Box>
                  </Paper>
                </Fade>
              </Modal>
            </TabPanel>

            {/* My Vouchers Tab */}
            <TabPanel value={tabValue} index={5}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                My Vouchers
              </Typography>

              {vouchersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress sx={{ color: 'var(--primary)' }} />
                </Box>
              ) : vouchersError ? (
                <Alert severity="error" sx={{ mb: 3 }}>{vouchersError}</Alert>
              ) : vouchersData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <VoucherIcon sx={{ fontSize: 56, color: '#ccc', mb: 2 }} />
                  <Typography color="text.secondary">You have no vouchers yet.</Typography>
                  <Button variant="contained" sx={{ mt: 2, backgroundColor: 'var(--button-primary)' }} onClick={() => navigate('/evoucher')}>
                    Buy an E-Voucher
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {vouchersData.map((voucher) => (
                    <Grid item xs={12} sm={6} key={voucher._id}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography sx={{ fontWeight: 600 }}>{voucher.voucherNumber}</Typography>
                          <Chip
                            label={voucher.status}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              color: voucher.status === 'ACTIVE' ? '#17ae7b' : '#3da2f8',
                              backgroundColor: voucher.status === 'ACTIVE' ? 'rgba(23,174,123,0.1)' : 'rgba(61,162,248,0.1)',
                            }}
                          />
                        </Box>
                        {voucher.campaignName && (
                          <Typography variant="body2" color="text.secondary">{voucher.campaignName}</Typography>
                        )}
                        <Typography sx={{ fontWeight: 700 }}>
                          KSH {Number(voucher.amount || 0).toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Clear Wishlist Confirmation Dialog */}
      <Dialog
        open={clearWishlistConfirmOpen}
        onClose={handleClearWishlistCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 380,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            bgcolor: alpha('#f44336', 0.08),
            borderBottom: '2px solid',
            borderColor: 'divider',
            pb: 2,
          }}
        >
          Clear Entire Wishlist?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Are you sure you want to clear your entire wishlist? This action cannot be undone. All your saved items will be permanently removed.
          </Typography>
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: alpha('#f44336', 0.05),
              borderLeft: '4px solid #f44336',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500 }}>
              ⚠️ You have {wishlistData.length} item{wishlistData.length !== 1 ? 's' : ''} in your wishlist.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            pt: 2,
            px: 3,
            pb: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={handleClearWishlistCancel}
            variant="outlined"
            disabled={isClearing}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearWishlistConfirm}
            variant="contained"
            color="error"
            disabled={isClearing}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            {isClearing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                Clearing...
              </>
            ) : (
              'Clear All'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default Account;